import type { PriceCheckerDevice, PriceCheckerRegistration } from '@pos/types';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Btn } from '../../components/Btn';
import { Field } from '../../components/Field';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Select } from '../../components/Select';
import { DataTable } from '../../components/Table';
import { listBranches } from '../../data/fetchers';
import { dashboardApi } from '../../lib/apiClient';
import { visibleBranchIds } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import { OperationsHeader } from '../operations/OperationsHeader';

export function PriceCheckerManagementView() {
  const user = useSession((state) => state.user);
  const branches = useQuery({ queryKey: ['branches'], queryFn: listBranches });
  const allowed = user
    ? visibleBranchIds(
        user,
        (branches.data ?? []).map((branch) => branch.id),
      )
    : [];
  const [branchId, setBranchId] = useState('');
  const [label, setLabel] = useState('จุดเช็กราคา 1');
  const [created, setCreated] = useState<PriceCheckerRegistration | null>(null);
  const [deviceId, setDeviceId] = useState('');
  const [device, setDevice] = useState<PriceCheckerDevice | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  async function perform(action: () => Promise<void>) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await action();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'ดำเนินการไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-6 min-w-0">
      <OperationsHeader
        title="จุดเช็กราคา"
        subtitle="จัดการเครื่องและสิทธิ์การเช็กราคาสำหรับลูกค้า"
      />
      <p>
        สร้างรหัสบนเครื่องผู้จัดการ แล้วเปิด <strong>/price-checker</strong>{' '}
        บนคอมสำหรับลูกค้าเพื่อกรอกรหัส เครื่องลูกค้าไม่ต้องเข้าสู่ระบบ Dashboard
      </p>
      {error && (
        <p role="alert" className="text-danger">
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      <form
        className="bo-card p-4 sm:p-6 space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void perform(async () => {
            setCreated(null);
            const result = await dashboardApi.createPriceChecker({
              branchId,
              label,
            });
            setCreated(result);
            setDeviceId(result.deviceId);
            setDevice(result);
          });
        }}
      >
        <h2 className="text-lg font-bold">เพิ่มเครื่องเช็กราคา</h2>
        <Field label="สาขา" className="min-w-0">
          <Select
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
            required
            disabled={busy}
          >
            <option value="">เลือกสาขา</option>
            {(branches.data ?? [])
              .filter((branch) => allowed.includes(branch.id))
              .map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
          </Select>
        </Field>
        {branches.isError && (
          <p role="alert">
            โหลดสาขาไม่สำเร็จ{' '}
            <Btn type="button" onClick={() => void branches.refetch()}>
              ลองใหม่
            </Btn>
          </p>
        )}
        <Field label="ชื่อเครื่อง" className="min-w-0">
          <Input
            className="w-full"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            maxLength={100}
            required
            disabled={busy}
          />
        </Field>
        <Btn kind="primary" disabled={busy || !branchId || !label.trim()}>
          สร้างรหัสติดตั้ง
        </Btn>
      </form>
      {created && (
        <section className="bg-ok-light border border-line rounded-card p-6 space-y-3">
          <h2 className="font-bold">
            รหัสติดตั้งสำหรับ {created.label} · {created.branchName}
          </h2>
          <p>
            รหัสนี้แสดงครั้งเดียว เก็บไว้เฉพาะบนเครื่องเช็กราคา ห้ามใส่ใน URL
          </p>
          <label className="block">
            รหัสติดตั้ง
            <textarea
              className="w-full border p-3 font-mono"
              readOnly
              value={created.token}
              onFocus={(event) => event.target.select()}
            />
          </label>
          <p>
            หมายเลขเครื่อง: <code>{created.deviceId}</code>
          </p>
          <p>
            สิทธิ์หมดอายุ{' '}
            {new Date(created.expiresAt).toLocaleDateString('th-TH')}{' '}
            จากนั้นสร้างรหัสใหม่และเพิกถอนเครื่องเดิม
          </p>
          <Btn
            type="button"

            onClick={() => setCreated(null)}
          >
            ซ่อนรหัส
          </Btn>
        </section>
      )}
      <section className="bo-card p-4 sm:p-6 space-y-4">
        <h2 className="text-lg font-bold">ตรวจสอบ / เพิกถอนสิทธิ์เครื่อง</h2>
        <p>ใช้หมายเลขเครื่องที่แสดงด้านล่างจอลูกค้า</p>
        <form
          className="grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_auto]"
          onSubmit={(event) => {
            event.preventDefault();
            void perform(async () => {
              setDevice(null);
              setDevice(await dashboardApi.getPriceChecker(deviceId.trim()));
            });
          }}
        >
          <Input
            aria-label="หมายเลขเครื่อง"
            className="w-full min-w-0"
            disabled={busy}
            value={deviceId}
            onChange={(event) => {
              setDeviceId(event.target.value);
              setDevice(null);
            }}
            required
            maxLength={36}
          />
          <Btn disabled={busy}>ตรวจสอบ</Btn>
        </form>
        {device && (
          <DataTable
            pageSize={Number.MAX_SAFE_INTEGER}
            mobileCards
            data={[device]}
            onRowClick={() => setDetailOpen(true)}
            columns={[
              { accessorKey: 'label', header: 'ชื่อเครื่อง' },
              { accessorKey: 'branchName', header: 'สาขา' },
              { accessorKey: 'deviceId', header: 'หมายเลขเครื่อง' },
            ]}
          />
        )}
      </section>
      <Modal
        variant="drawer"
        className="!my-0"
        title="รายละเอียดจุดเช็กราคา"
        open={detailOpen && !!device}
        onClose={() => setDetailOpen(false)}
      >
        {device && (
          <div className="space-y-4">
            <h2 className="font-bold">
              {device.label} · {device.branchName}
            </h2>
            <p className="break-all">{device.deviceId}</p>
            <p>เพิกถอนแล้วเครื่องนี้จะเช็กราคาไม่ได้ในการเรียกครั้งถัดไป</p>
            <Btn
              kind="danger"
              disabled={busy}
              onClick={() => setConfirmRevoke(true)}
            >
              เพิกถอนสิทธิ์เครื่องนี้
            </Btn>
          </div>
        )}
      </Modal>
      <Modal
        className="!my-auto"
        title="ยืนยันเพิกถอนสิทธิ์เครื่อง"
        tone="danger"
        open={confirmRevoke && !!device}
        onClose={() => setConfirmRevoke(false)}
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Btn disabled={busy} onClick={() => setConfirmRevoke(false)}>
              กลับ
            </Btn>
            <Btn
              kind="danger"
              disabled={busy}
              onClick={() =>
                void perform(async () => {
                  if (!device) return;
                  await dashboardApi.revokePriceChecker(device.deviceId);
                  if (created?.deviceId === device.deviceId) setCreated(null);
                  setDevice(null);
                  setDetailOpen(false);
                  setConfirmRevoke(false);
                  setNotice('เพิกถอนสิทธิ์เครื่องแล้ว');
                })
              }
            >
              ยืนยันเพิกถอนสิทธิ์
            </Btn>
          </div>
        }
      >
        <p>เครื่องนี้จะไม่สามารถเช็กราคาได้หลังยืนยัน</p>
        {error && (
          <p role="alert" className="text-danger">
            {error}
          </p>
        )}
      </Modal>
    </div>
  );
}
